import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";
import { PublicLayout } from "../components/public/PublicLayout";
import { Clock, ChefHat } from "lucide-react";

const FILTERS = ["Breakfast", "Lunch", "Dinner", "Snacks", "Quick Recipes", "Traditional Brazilian"];

export default function Recipes() {
  const { t, tr } = useLang();
  const navigate = useNavigate();
  const [filter, setFilter] = useState("");
  useEffect(() => { document.title = "Recipes | Sambiva"; }, []);

  const { data: recipes = [], isLoading } = useQuery({
    queryKey: ["recipes", filter],
    queryFn: async () => (await api.get(`/recipes${filter ? `?category=${encodeURIComponent(filter)}` : ""}`)).data,
  });

  return (
    <PublicLayout>
      <section className="bg-white border-b border-sambiva-charcoal/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
          <p className="font-script text-2xl text-sambiva-red mb-1">Make something delicious</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-sambiva-charcoal mb-3">Recipes</h1>
          <p className="text-sambiva-muted text-lg max-w-2xl">Simple, authentic ways to bring Brazil to your kitchen.</p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-wrap gap-2 mb-8">
          <button data-testid="recipe-filter-all" onClick={() => setFilter("")} className={`text-sm px-4 py-2 rounded-full font-medium ${!filter ? "bg-sambiva-red text-white" : "bg-white text-sambiva-charcoal hover:bg-sambiva-red/10"}`}>All</button>
          {FILTERS.map((f) => (
            <button key={f} data-testid={`recipe-filter-${f.replace(/\s+/g, "-")}`} onClick={() => setFilter(f)} className={`text-sm px-4 py-2 rounded-full font-medium ${filter === f ? "bg-sambiva-red text-white" : "bg-white text-sambiva-charcoal hover:bg-sambiva-red/10"}`}>{f}</button>
          ))}
        </div>

        {isLoading ? (
          <div className="text-center py-20 text-sambiva-muted">{t("common.loading")}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="recipes-grid">
            {recipes.map((r) => (
              <div key={r.id} data-testid={`recipe-card-${r.slug}`} onClick={() => navigate(`/recipes/${r.slug}`)} className="group bg-white rounded-3xl overflow-hidden card-hover cursor-pointer border border-sambiva-charcoal/5">
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={r.image} alt={tr(r.translations, "title", r.title)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                </div>
                <div className="p-5">
                  <h3 className="font-serif text-xl text-sambiva-charcoal mb-2">{tr(r.translations, "title", r.title)}</h3>
                  <p className="text-sm text-sambiva-muted line-clamp-2 mb-3">{r.description}</p>
                  <div className="flex items-center gap-4 text-sm text-sambiva-muted">
                    <span className="flex items-center gap-1"><Clock size={14} /> {r.prep_time}</span>
                    <span className="flex items-center gap-1"><ChefHat size={14} /> {r.difficulty}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
